import '@fontsource/poppins';
import axios from 'axios';

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { handleDeleteAccount } from '@src/api/Users';
import { DeleteButton, ViewButton } from '@src/components/buttons';
import { useAuth } from '@src/contexts/authContext';

import { uuid } from '@utils';

const url = `${process.env.REACT_APP_API_URL}`;

export default function TableNurseUnverified() {
  const navigate = useNavigate();
  const format_date = { year: 'numeric', month: 'short', day: 'numeric' };
  const getInitialPage = () => {
    const storedPage = sessionStorage.getItem('nurseUnverifiedPage');
    return storedPage ? parseInt(storedPage, 10) : 0;
  };
  const getInitialRowsPerPage = () => {
    const storedRows = sessionStorage.getItem('nurseUnverifiedRowsPerPage');
    return storedRows ? parseInt(storedRows, 10) : 10;
  };

  const [page, setPage] = React.useState(getInitialPage());
  const [rowsPerPage, setRowsPerPage] = React.useState(getInitialRowsPerPage());

  const handleClickDetail = (userId) => {
    sessionStorage.setItem('nurseUnverifiedPage', page);
    sessionStorage.setItem('nurseUnverifiedRowsPerPage', rowsPerPage);
    navigate(`/unverified-nurse-detail/${userId}`);
  };
  const [loading, setLoading] = useState();

  const [list, setList] = useState([]);
  const { auth } = useAuth();

  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to first page on new search
  };

  const [sortOrder, setSortOrder] = useState('desc');
  const [sortedList, setSortedList] = useState([]);

  useEffect(() => {
    const filteredList = list?.filter((row) => {
      const lowerCaseSearchQuery = searchQuery.toLowerCase();
      return (
        (row &&
          row.full_name &&
          row.full_name.toLowerCase().includes(lowerCaseSearchQuery)) ||
        (row?.email &&
          row.email.toLowerCase().includes(lowerCaseSearchQuery)) ||
        (row?.phone_number &&
          row.phone_number.toLowerCase().includes(lowerCaseSearchQuery))
      );
    });
    const sortedData = [...filteredList].sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    setSortedList(sortedData);
  }, [list, searchQuery, sortOrder]);

  const handleSortToggle = () => {
    setSortOrder((prevOrder) => (prevOrder === 'desc' ? 'asc' : 'desc'));
  };

  useEffect(() => {
    sessionStorage.setItem('nurseUnverifiedPage', page);
  }, [page]);
  useEffect(() => {
    sessionStorage.setItem('nurseUnverifiedRowsPerPage', rowsPerPage);
  }, [rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const [status, setStatus] = useState('created');
  const [statuses, setStatuses] = useState('pending');
  const [deleteStatus, setDeleteStatus] = useState();
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${url}/admin/users?role=nurse&status=${status}&status=${statuses}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.idToken}`,
            },
          },
        );
        setList(response.data);
        setLoading(false);
      } catch (error) {
        console.log('Error', error.message);
        setLoading(false);
      }
    };

    if (auth?.idToken) {
      fetchData();
    }
  }, [auth, status, statuses, deleteStatus]);

  const handleDelete = async () => {
    const formData = {
      email: list.find((item) => item._id === deleteId)?.email,
    };

    try {
      const response = await axios.post(
        `${url}/admin/v3/firebase/delete-user`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${auth.idToken}`,
            'My-Custom-Header': 'foobar',
          },
        },
      );
      console.log(response);
      setDeleteStatus('Delete successful');
    } catch (error) {
      console.error(error);
    }
  };

  const showDeleteConfirmationDialog = (id) => {
    setDeleteId(id);
    setShowDeleteConfirmation(true);
  };

  const handleConfirmationDialogClose = () => {
    setShowDeleteConfirmation(false);
  };

  const handleConfirmationDialogConfirm = () => {
    handleDelete(deleteId);
    handleDeleteAccount(deleteId, auth.idToken, setDeleteStatus);
    setShowDeleteConfirmation(false);
  };
  return (
    <div>
      <Box sx={{ width: '100%' }} className='flex w-full justify-end py-4'>
        <TextField
          fullWidth
          size='medium'
          variant='outlined'
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              height: '50px',
            },
            mt: '5px',
            width: '300px',
          }}
          onChange={handleSearch}
          placeholder='Search by name, email or phone'
        />
      </Box>
      {loading ? (
        <div className='font-poppins py-6 px-8 text-gray-500'>Loading...</div>
      ) : (
        <span className='flex w-full flex-col items-start py-3'>
          <TableContainer
            className='rounded-xl'
            component={Paper}
            style={{
              width: '100%',
              paddingBottom: '12px',
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label='simple table'>
              <TableHead>
                <TableRow>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    // style={{ width: '220px' }}
                  >
                    Name
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0 }}
                  >
                    Phone Number
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0 }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0, width: '150px' }}
                  >
                    <TableSortLabel
                      active
                      direction={sortOrder}
                      onClick={handleSortToggle}
                      className='text-xs font-bold text-gray-400'
                      sx={{ width: '50px' }}
                    >
                      Registered Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 0, width: '100px' }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    className='text-xs font-bold text-gray-400'
                    style={{ padding: 4, width: '170px' }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(sortedList) && sortedList.length > 0 ? (
                  sortedList
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => (
                      <TableRow
                        key={row?._id || `${row?.name}-${uuid()}`}
                        hover
                        role='checkbox'
                        tabIndex={-1}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                        }}
                      >
                        <TableCell
                          component='th'
                          scope='row'
                          className='border-white text-sm font-bold text-gray-500'
                        >
                          <span
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                            }}
                          >
                            <Avatar alt='' src={`${url}${row?.profile_pict}`} />
                            <span
                              style={{
                                display: 'inline-block',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'bottom',
                              }}
                              title={row?.full_name || 'No name added'}
                            >
                              {row?.full_name || 'No name added'}
                            </span>
                          </span>
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          {row?.phone_number}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{
                            padding: 0,
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={row?.email}
                        >
                          {row?.email}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          {/* {row?.createdAt
                          ? `${new Date(row?.createdAt).toLocaleDateString(
                              'au-AU',
                              format_date,
                            )} | ${formatTime(row?.createdAt)}`
                          : 'No date and time added'} */}

                          {row?.createdAt
                            ? `${new Date(row?.createdAt).toLocaleDateString(
                                'au-AU',
                                format_date,
                              )}`
                            : 'No date added'}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          {row?.status}
                        </TableCell>
                        <TableCell
                          className='border-white text-sm font-bold text-gray-500'
                          style={{ padding: 0 }}
                        >
                          <ViewButton
                            onClick={() => handleClickDetail(row?._id)}
                          />
                          &nbsp; &nbsp;
                          <DeleteButton
                            onClick={() =>
                              showDeleteConfirmationDialog(row?._id)
                            }
                          />
                          {showDeleteConfirmation && (
                            <Dialog
                              open={showDeleteConfirmation}
                              onClose={handleConfirmationDialogClose}
                            >
                              <DialogTitle>Confirm Delete</DialogTitle>
                              <DialogContent>
                                <DialogContentText>
                                  Are you sure you want to delete this account?
                                </DialogContentText>
                              </DialogContent>
                              <DialogActions>
                                <Button
                                  onClick={handleConfirmationDialogClose}
                                  color='primary'
                                >
                                  Cancel
                                </Button>
                                <Button
                                  onClick={handleConfirmationDialogConfirm}
                                  color='error'
                                >
                                  Delete
                                </Button>
                              </DialogActions>
                            </Dialog>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell
                      className='border border-white'
                      colSpan={6}
                      align='center'
                    >
                      <div className='font-poppins py-6 text-gray-500'>
                        No nurses listed
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Box
              className='flex justify-end py-3'
              sx={{ width: '100%', fontFamily: 'Poppins' }}
            >
              <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component='div'
                count={list.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </TableContainer>
        </span>
      )}
    </div>
  );
}
